/**
 * 2D 圖面的「主要範圍」：略過遠處零散物件後，內容最集中的那一塊。
 *
 * CAD 圖常有遠離圖框的殘留物件（舊圖塊、定位點、誤拉的線），全圖範圍會被它們
 * 撐大，開圖時真正的圖框只剩一個小點。做法是把物件中心丟進格網，找 8 方向相連、
 * 物件最多的一群；這群要佔大部分物件，且比目前的取景明顯小，才採用。
 */

const SELECTOR = 'path, polyline, polygon, line, circle, ellipse, rect, text, use, image';

/**
 * @param {Array<{x:number,y:number,w:number,h:number}>} boxes 物件外框（根座標）
 * @param {{x:number,y:number,w:number,h:number}} view 目前「全圖」取景
 * @returns {{x:number,y:number,w:number,h:number}|null} null 表示維持全圖
 */
export function mainContentBox(boxes, view, { grid = 32, minShare = 0.6, maxArea = 0.5, padding = 1.06 } = {}) {
  const valid = boxes.filter(b => [b.x, b.y, b.w, b.h].every(Number.isFinite) && b.w >= 0 && b.h >= 0);
  if (valid.length < 2 || !view?.w || !view?.h) return null;
  const all = union(valid);
  const span = Math.max(all.w, all.h);
  if (!(span > 0)) return null;
  const cw = (all.w || span) / grid, ch = (all.h || span) / grid;
  const col = x => Math.max(0, Math.min(grid - 1, Math.floor((x - all.x) / cw)));
  const row = y => Math.max(0, Math.min(grid - 1, Math.floor((y - all.y) / ch)));
  // 圖框等物件要讓它覆蓋的格子相連；但跨越大半張圖的物件（遠處拉回來的長線）只算中心。
  const reach = Math.max(2, grid / 4);
  const byCenter = new Map(), occupied = new Set();
  for (const box of valid) {
    const key = row(box.y + box.h / 2) * grid + col(box.x + box.w / 2);
    if (!byCenter.has(key)) byCenter.set(key, []);
    byCenter.get(key).push(box);
    occupied.add(key);
    const i0 = col(box.x), i1 = col(box.x + box.w), j0 = row(box.y), j1 = row(box.y + box.h);
    if (i1 - i0 > reach || j1 - j0 > reach) continue;
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) occupied.add(j * grid + i);
  }
  const seen = new Set();
  let best = [];
  for (const start of occupied) {
    if (seen.has(start)) continue;
    seen.add(start);
    const stack = [start], members = [];
    while (stack.length) {
      const key = stack.pop(), i = key % grid, j = Math.floor(key / grid);
      if (byCenter.has(key)) members.push(...byCenter.get(key));
      for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
        const ni = i + di, nj = j + dj, next = nj * grid + ni;
        if (ni < 0 || nj < 0 || ni >= grid || nj >= grid || seen.has(next) || !occupied.has(next)) continue;
        seen.add(next); stack.push(next);
      }
    }
    if (members.length > best.length) best = members;
  }
  if (best.length / valid.length < minShare) return null;
  const main = union(best);
  const w = Math.max(main.w, span * 1e-3) * padding, h = Math.max(main.h, span * 1e-3) * padding;
  if (w * h >= view.w * view.h * maxArea) return null;
  return { x: main.x + main.w / 2 - w / 2, y: main.y + main.h / 2 - h / 2, w, h };
}

/** 讀取 SVG 內每個可繪製物件在根座標系的外框；物件太多時均勻取樣。 */
export function elementBoxes(svg, limit = 20000) {
  const root = svg.getScreenCTM?.();
  if (!root) return [];
  let inverse;
  try { inverse = root.inverse(); } catch { return []; }
  const elements = svg.querySelectorAll(SELECTOR);
  const stride = Math.max(1, Math.ceil(elements.length / limit));
  const boxes = [];
  for (let n = 0; n < elements.length; n += stride) {
    const el = elements[n];
    if (el.closest('defs, clipPath, mask, symbol, pattern')) continue;
    let b, m;
    try { b = el.getBBox(); m = el.getScreenCTM(); } catch { continue; }
    if (!m || (!b.width && !b.height)) continue;
    const t = inverse.multiply(m);
    const xs = [], ys = [];
    for (const [px, py] of [[b.x, b.y], [b.x + b.width, b.y], [b.x, b.y + b.height], [b.x + b.width, b.y + b.height]]) {
      xs.push(t.a * px + t.c * py + t.e); ys.push(t.b * px + t.d * py + t.f);
    }
    const x = Math.min(...xs), y = Math.min(...ys);
    boxes.push({ x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y });
  }
  return boxes;
}

function union(boxes) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const b of boxes) {
    x0 = Math.min(x0, b.x); y0 = Math.min(y0, b.y);
    x1 = Math.max(x1, b.x + b.w); y1 = Math.max(y1, b.y + b.h);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
